import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, onSnapshot, query, orderBy, limit, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { Edit, BrainCircuit, Activity, Clock, AlertTriangle, Trash2, ArrowUpDown, Filter, FileCheck, ChevronRight, ChevronDown, Users, List, ListChecks } from 'lucide-react';
import { getFunctions } from "firebase/functions";
import { useNavigate } from 'react-router-dom';
import AiChatModal from '../components/AiChatModal';
import SystemAiChatModal from '../components/SystemAiChatModal';
import {
    sheetProgress, jobBlocks, isJobBlocked, groupJobsByClient,
    daysToDelivery, openTasks, pendingFileCount, isFinalizado,
    materialDemand, materialShortages, lastLogSummary,
} from '../lib/dashboardGrouping.js';

// Fases del flujo de trabajo (mismo modelo que el StatusMenu). Orden: lo urgente primero.
const WORKFLOW_PHASES = [
    { value: 'URGENTE', label: 'Urgente', card: 'bg-yellow-50 border-yellow-300 text-yellow-800', dot: 'bg-yellow-500' },
    { value: 'En Progreso', label: 'En Progreso', card: 'bg-blue-50 border-blue-300 text-blue-800', dot: 'bg-blue-500' },
    { value: 'Pendiente', label: 'Pendiente', card: 'bg-gray-50 border-gray-300 text-gray-700', dot: 'bg-gray-400' },
    { value: 'En Hold', label: 'En Hold', card: 'bg-red-50 border-red-300 text-red-800', dot: 'bg-red-500' },
    { value: 'Pendiente por Facturar', label: 'Por Facturar', card: 'bg-purple-50 border-purple-300 text-purple-800', dot: 'bg-purple-500' },
    { value: 'Finalizado', label: 'Finalizado', card: 'bg-green-50 border-green-300 text-green-800', dot: 'bg-green-500' },
];

// Colores de las píldoras de bloqueo, por tipo (el `kind` que devuelve jobBlocks).
// El material lleva su id en el kind (`material:<id>`), de ahí el prefijo.
const BLOCK_STYLE = {
    hold: 'bg-red-100 text-red-700',
    alert: 'bg-red-100 text-red-700',
    returned: 'bg-purple-100 text-purple-700',
    files: 'bg-amber-100 text-amber-800',
};
const blockStyle = (kind) =>
    (kind || '').startsWith('material:') ? 'bg-orange-100 text-orange-800' : (BLOCK_STYLE[kind] || 'bg-gray-100 text-gray-700');

const KPICard = ({ title, value, icon, colorClass, active = false, onClick }) => (
    <div
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        className={`p-3 rounded-lg shadow-md flex items-center justify-between min-h-[90px] md:min-h-0 ${colorClass} ${onClick ? 'cursor-pointer transition-transform hover:scale-[1.02]' : ''} ${active ? 'ring-2 ring-offset-2 ring-gray-700' : ''}`}
    >
        <div>
            <p className="text-[11px] md:text-xs font-medium text-white/90 leading-tight">{title}</p>
            <p className="text-[26px] md:text-2xl font-bold text-white leading-none mt-1">{value}</p>
        </div>
        <div className="text-white/50">{icon}</div>
    </div>
);

const StatusMenu = ({ job, onStatusChange }) => {
    const [isOpen, setIsOpen] = useState(false);

    const statuses = [
        { label: 'En Progreso', value: 'En Progreso', color: 'text-blue-600', bg: 'bg-blue-100' },
        { label: 'Pendiente', value: 'Pendiente', color: 'text-gray-600', bg: 'bg-gray-100' },
        { label: 'Finalizado', value: 'Finalizado', color: 'text-green-600', bg: 'bg-green-100' },
        { label: 'Pendiente por Facturar', value: 'Pendiente por Facturar', color: 'text-purple-600', bg: 'bg-purple-100' },
        { label: 'En Hold', value: 'En Hold', color: 'text-red-600', bg: 'bg-red-100' },
        { label: 'URGENTE', value: 'URGENTE', color: 'text-yellow-600', bg: 'bg-yellow-100' }
    ];

    const currentStatus = statuses.find(s => s.value === (job.status?.phase || 'Pendiente')) || statuses[1];

    const handleSelect = (statusValue) => {
        onStatusChange(job.id, statusValue);
        setIsOpen(false);
    };

    return (
        <div className="relative inline-block text-left" onClick={(e) => e.stopPropagation()}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                onBlur={() => setTimeout(() => setIsOpen(false), 200)}
                className={`px-3 py-1 text-xs font-semibold rounded-full w-32 text-center transition-transform transform hover:scale-105 ${currentStatus.bg} ${currentStatus.color}`}
            >
                {currentStatus.label}
            </button>
            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                        {statuses.map(status => (
                            <a
                                key={status.value}
                                href="#"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSelect(status.value); }}
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                <span className={`mr-2 inline-block h-2 w-2 rounded-full ${status.bg}`}></span>
                                {status.label}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Barra de avance de una fila del detalle (impresión o corte).
const ProgressLine = ({ label, done, total, barClass }) => {
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return (
        <div className="flex items-center gap-2 mb-1">
            <span className="w-10 text-[10px] text-gray-500">{label}</span>
            <span className="flex-1 max-w-[110px] h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <span className={`block h-full ${barClass}`} style={{ width: `${pct}%` }} />
            </span>
            <span className="font-mono text-[11px] text-gray-600 tabular-nums">
                {total > 0 ? `${done}/${total}` : '—'}
            </span>
        </div>
    );
};

// Fila expandida de un job: motivo de bloqueo + la lista COMPLETA de tareas
// abiertas. El avance de planchas y el nº de tareas ya viven en la fila misma
// (inline), así que aquí no se repiten.
const JobDetailRow = ({ job, data, colSpan, blockOpts }) => {
    if (!data || data.loading) {
        return (
            <tr className="bg-gray-50">
                <td colSpan={colSpan} className="px-8 py-3 text-xs text-gray-500 italic">Cargando detalle del trabajo…</td>
            </tr>
        );
    }
    if (data.error) {
        return (
            <tr className="bg-gray-50">
                <td colSpan={colSpan} className="px-8 py-3 text-xs text-red-600">No se pudo cargar el detalle: {data.error}</td>
            </tr>
        );
    }

    const abiertas = openTasks(data.tasks);
    const bloqueos = jobBlocks(job, data, blockOpts);
    const ambar = pendingFileCount(data.sheets);

    return (
        <tr className="bg-gray-50 border-b border-gray-200">
            <td colSpan={colSpan} className="px-8 py-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Bloqueado por</p>
                        {bloqueos.length === 0 ? (
                            <p className="text-xs font-semibold text-green-600">✓ Sin bloqueos — avanza normal</p>
                        ) : (
                            <div className="flex flex-wrap gap-1.5">
                                {bloqueos.map((b) => (
                                    <span key={b.kind} className={`inline-block text-[11px] font-semibold px-2 py-1 rounded ${blockStyle(b.kind)}`}>
                                        {b.label}
                                    </span>
                                ))}
                            </div>
                        )}
                        {ambar > 0 && (
                            <p className="text-[11px] text-amber-700 mt-2">{ambar} plancha{ambar === 1 ? '' : 's'} sin archivo, aún en prepress</p>
                        )}
                    </div>

                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                            Tareas abiertas {abiertas.length > 0 && <span className="font-normal normal-case">({abiertas.length})</span>}
                        </p>
                        {abiertas.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">Sin tareas abiertas</p>
                        ) : (
                            abiertas.map((t) => (
                                <div key={t.id} className="flex items-center gap-2 mb-1">
                                    <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-full flex-shrink-0 ${t.type === 'system' || t.type === 'system_reproduction' ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'}`}>
                                        {t.assignedToName || t.assignedToEmail || 'sin asignar'}
                                    </span>
                                    <span className="text-xs text-gray-700 truncate">{t.title || t.description || '(sin título)'}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </td>
        </tr>
    );
};

const DashboardView = ({ db, user }) => {
    const [jobs, setJobs] = useState([]);
    const [clients, setClients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [isJobChatModalOpen, setIsJobChatModalOpen] = useState(false);
    const [selectedJobForChat, setSelectedJobForChat] = useState(null);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');
    const [statusFilter, setStatusFilter] = useState('Todos');

    const [isSystemChatOpen, setIsSystemChatOpen] = useState(false); // asistente del sistema
    const [lightbox, setLightbox] = useState(null); // { url, title } para ver el preview en grande

    // ── Centro de Comando ──────────────────────────────────────────────────
    // Vista agrupada por cliente (default) o la lista plana de siempre.
    const [groupBy, setGroupBy] = useState('client');
    // Los 51 Finalizado (de 62 jobs) quedan FUERA por default — decisión de Daniel
    // 2026-09-01. El toggle los trae, y el chip "Finalizado" del flujo también.
    const [includeFinished, setIncludeFinished] = useState(false);
    const [onlyBlocked, setOnlyBlocked] = useState(false);
    const [expandedJobs, setExpandedJobs] = useState(() => new Set());
    const [collapsedClients, setCollapsedClients] = useState(() => new Set());
    // { [jobId]: { loading, sheets, tasks, lastLog, error } } — subcolecciones por job.
    const [enrich, setEnrich] = useState({});
    const enrichRef = useRef({});
    // Catálogo de materiales, para la regla de falta de stock.
    const [materials, setMaterials] = useState([]);
    // Jobs a los que ya se les aplicó la auto-expansión por bloqueo: se hace UNA
    // vez por job, para que colapsar a mano no se deshaga en el siguiente render.
    const autoExpandedRef = useRef(new Set());

    const functions = getFunctions();
    const navigate = useNavigate();

    // Cerrar el lightbox con la tecla Esc.
    useEffect(() => {
        if (!lightbox) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightbox]);
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

    useEffect(() => {
        if (!db) return;
        setIsLoading(true);

        const fetchClients = async () => {
            const clientsSnapshot = await getDocs(collection(db, "clients"));
            setClients(clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        };

        // Materiales: los necesita la regla de falta de stock. Lectura única —
        // `materials` es un catálogo de 47 docs que casi no cambia.
        const fetchMaterials = async () => {
            try {
                const snap = await getDocs(collection(db, "materials"));
                setMaterials(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                // Sin catálogo no se puede evaluar el stock: el resto del dashboard
                // sigue funcionando y simplemente no aparece la píldora de material.
                console.error("Error cargando materiales:", error);
            }
        };

        const jobsCollectionRef = collection(db, 'jobs');
        const q = query(jobsCollectionRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const jobsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setJobs(jobsData);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching jobs:", error);
            setIsLoading(false);
        });

        fetchClients();
        fetchMaterials();
        return () => unsubscribe();
    }, [db]);

    // Carga las subcolecciones de un job (planchas + tareas). UNA sola vez por job:
    // `enrichRef` evita relanzar la lectura en cada render. Se usa la ruta ANIDADA
    // (`jobs/{id}/sheets`) y no un collectionGroup: las rules abren la anidada a
    // cualquier autenticado, mientras que el collectionGroup exige canScanProduction
    // (firestore.rules) y además no tiene índice para estos campos.
    const loadJobDetail = useCallback(async (jobId) => {
        if (!db || !jobId || enrichRef.current[jobId]) return;
        enrichRef.current[jobId] = true;
        setEnrich(prev => ({ ...prev, [jobId]: { loading: true } }));
        try {
            const [sheetsSnap, tasksSnap, logSnap] = await Promise.all([
                getDocs(collection(db, `jobs/${jobId}/sheets`)),
                getDocs(collection(db, `jobs/${jobId}/tasks`)),
                // Último evento del bus por job, para la columna Notas. Shape
                // verificado en prod: { event, details, user, timestamp }.
                getDocs(query(collection(db, `jobs/${jobId}/ai_logs`), orderBy('timestamp', 'desc'), limit(1))),
            ]);
            setEnrich(prev => ({
                ...prev,
                [jobId]: {
                    loading: false,
                    sheets: sheetsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
                    tasks: tasksSnap.docs.map(d => ({ id: d.id, ...d.data() })),
                    lastLog: logSnap.docs.length ? logSnap.docs[0].data() : null,
                },
            }));
        } catch (error) {
            console.error(`Error cargando detalle del job ${jobId}:`, error);
            enrichRef.current[jobId] = false;   // permite reintentar al re-expandir
            setEnrich(prev => ({ ...prev, [jobId]: { loading: false, error: error.message } }));
        }
    }, [db]);

    // Enriquecimiento AUTOMÁTICO solo de los jobs vivos (11 de 62 en producción):
    // es lo que alimenta el KPI "Bloqueados" y el contador por cliente. Los
    // finalizados NO se tocan aquí — se cargan al expandirlos, si se muestran.
    useEffect(() => {
        if (!db || jobs.length === 0) return;
        jobs.filter(j => !isFinalizado(j)).forEach(j => { loadJobDetail(j.id); });
    }, [db, jobs, loadJobDetail]);

    // Falta de material: Σ(repetitions − printedCount) por materialId sobre los
    // jobs VIVOS, contra `materials.stock.quantity`. El shape se verificó contra
    // producción el 2026-09-02 (quantity numérico en 47/47); si algún doc no lo
    // trae, ese material NO bloquea.
    const blockOpts = useMemo(() => {
        const vivos = jobs.filter(j => !isFinalizado(j)).map(j => j.id);
        return { shortages: materialShortages(materialDemand(enrich, vivos), materials) };
    }, [jobs, enrich, materials]);

    // Los jobs con bloqueo arrancan expandidos. Solo la primera vez que se
    // detecta el bloqueo: si el usuario colapsa la fila, se queda colapsada.
    useEffect(() => {
        const abrir = jobs.filter(j =>
            !isFinalizado(j)
            && !autoExpandedRef.current.has(j.id)
            && enrich[j.id] && !enrich[j.id].loading
            && isJobBlocked(j, enrich[j.id], blockOpts),
        );
        if (abrir.length === 0) return;
        abrir.forEach(j => autoExpandedRef.current.add(j.id));
        setExpandedJobs(prev => {
            const next = new Set(prev);
            abrir.forEach(j => next.add(j.id));
            return next;
        });
    }, [jobs, enrich, blockOpts]);

    const getClientName = useCallback((clientId) => {
        const client = clients.find(c => c.id === clientId);
        return client ? client.name : 'N/A';
    }, [clients]);

    const handleOpenJobChat = (job) => { setSelectedJobForChat(job); setIsJobChatModalOpen(true); };
    const handleJobSelect = (jobId) => { navigate(`/jobs/${jobId}`); };
    const handleEditJob = (job) => { navigate(`/jobs/edit/${job.id}`); };

    const toggleExpanded = (jobId) => {
        setExpandedJobs(prev => {
            const next = new Set(prev);
            if (next.has(jobId)) next.delete(jobId);
            else { next.add(jobId); loadJobDetail(jobId); }
            return next;
        });
    };

    const toggleClient = (clientId) => {
        setCollapsedClients(prev => {
            const next = new Set(prev);
            if (next.has(clientId)) next.delete(clientId); else next.add(clientId);
            return next;
        });
    };

    const handleStatusChange = async (jobId, newStatus) => {
        const jobRef = doc(db, 'jobs', jobId);
        try {
            const newStage = (newStatus === 'Finalizado' || newStatus === 'En Hold' || newStatus === 'Pendiente por Facturar') ? newStatus.toUpperCase() : 'FASE';
            await updateDoc(jobRef, { 'status.stage': newStage, 'status.phase': newStatus });
        } catch (error) { console.error("Error updating status:", error); alert("No se pudo actualizar el estado."); }
    };

    const handleDeleteJob = async (jobId) => {
        if (window.confirm("¿Estás seguro de que quieres eliminar este trabajo? Esta acción no se puede deshacer.")) {
            const jobRef = doc(db, 'jobs', jobId);
            try { await deleteDoc(jobRef); }
            catch (error) { console.error("Error deleting job: ", error); alert("No se pudo eliminar el trabajo."); }
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDirection('asc');
        }
    };

    const dashboardKPIs = useMemo(() => {
        const now = new Date();
        const sevenDaysFromNow = new Date();
        sevenDaysFromNow.setDate(now.getDate() + 7);
        const activeJobs = jobs.filter(j => j.status?.phase !== 'Finalizado' && j.status?.phase !== 'Pendiente por Facturar').length;
        const jobsAtRisk = jobs.filter(j => {
            const deliveryDate = j.deliveryDate ? new Date(j.deliveryDate) : null;
            return deliveryDate && deliveryDate <= sevenDaysFromNow && j.status?.phase !== 'Finalizado' && j.status?.phase !== 'Pendiente por Facturar';
        }).length;
        // "Bloqueados" reemplaza a "Trabajos Detenidos": el conteo de En Hold sigue
        // visible en la tira de Flujo de Trabajo, y este KPI lo incluye además de
        // alertas, devoluciones y planchas en rojo. Solo sobre jobs no finalizados,
        // que son los únicos que se enriquecen.
        const blocked = jobs.filter(j => !isFinalizado(j) && isJobBlocked(j, enrich[j.id] || {}, blockOpts)).length;
        const pendingInvoice = jobs.filter(j => j.status?.phase === 'Pendiente por Facturar').length;
        return { activeJobs, jobsAtRisk, blocked, pendingInvoice };
    }, [jobs, enrich, blockOpts]);

    // Flujo de trabajo: conteo de trabajos por fase (para la tira clickeable).
    const workflowByPhase = useMemo(() => {
        const counts = {};
        WORKFLOW_PHASES.forEach(p => { counts[p.value] = 0; });
        jobs.forEach(j => {
            const phase = j.status?.phase || 'Pendiente';
            counts[phase] = (counts[phase] || 0) + 1;
        });
        return counts;
    }, [jobs]);

    // Throughput motivacional: finalizados en los últimos 30 días.
    const throughput30d = useMemo(() => {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 30);
        return jobs.filter(j => j.status?.phase === 'Finalizado' && j.createdAt?.toDate && j.createdAt.toDate() >= cutoff).length;
    }, [jobs]);

    const sortedAndFilteredJobs = useMemo(() => {
        // Pedir el chip/filtro "Finalizado" los trae aunque el toggle esté apagado:
        // si no, el filtro devolvería una lista vacía sin explicación.
        const showFinished = includeFinished || statusFilter === 'Finalizado';
        return jobs
            .filter(job => {
                const searchMatch = (job.jobName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                                    (job.id?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                                    (String(job.jobNumber) || '').includes(searchTerm) ||
                                    (getClientName(job.clientId).toLowerCase() || '').includes(searchTerm.toLowerCase());
                const statusMatch = statusFilter === 'Todos' || (job.status?.phase || 'Pendiente') === statusFilter;
                const finishedMatch = showFinished || !isFinalizado(job);
                const blockedMatch = !onlyBlocked || isJobBlocked(job, enrich[job.id] || {}, blockOpts);
                return searchMatch && statusMatch && finishedMatch && blockedMatch;
            })
            .sort((a, b) => {
                let valA, valB;
                if (sortBy === 'jobNumber') { valA = a.jobNumber || 0; valB = b.jobNumber || 0; }
                else if (sortBy === 'jobName') { valA = a.jobName || ''; valB = b.jobName || ''; }
                else { valA = a.createdAt?.toDate() || 0; valB = b.createdAt?.toDate() || 0; }
                if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
                if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
    }, [jobs, searchTerm, sortBy, sortDirection, statusFilter, getClientName, includeFinished, onlyBlocked, enrich, blockOpts]);

    const clientGroups = useMemo(
        () => groupJobsByClient(sortedAndFilteredJobs, getClientName, enrich, blockOpts),
        [sortedAndFilteredJobs, getClientName, enrich, blockOpts],
    );

    const hiddenFinishedCount = useMemo(
        () => (includeFinished || statusFilter === 'Finalizado' ? 0 : jobs.filter(isFinalizado).length),
        [jobs, includeFinished, statusFilter],
    );

    const SortableHeader = ({ field, label }) => (
        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="flex items-center justify-center gap-1 cursor-pointer" onClick={() => handleSort(field)}>
                {label} <ArrowUpDown size={14} />
            </div>
        </th>
    );

    const COL_COUNT = 10;   // las 9 de siempre + la columna del chevron de expansión

    // Fila de un job. Preview / Notas / Acciones quedan EXACTAMENTE como en prod.
    const renderJobRow = (job) => {
        const isFinished = job.status?.phase === 'Finalizado' || job.status?.phase === 'Pendiente por Facturar';
        const expanded = expandedJobs.has(job.id);
        const dias = daysToDelivery(job.deliveryDate);
        const entregaClass = dias === null ? 'text-red-500 italic'
            : dias < 0 ? 'text-red-600 font-bold'
            : dias <= 7 ? 'text-amber-600 font-semibold'
            : 'text-gray-600';
        const bloqueos = jobBlocks(job, enrich[job.id] || {}, blockOpts);
        const detalle = enrich[job.id];
        const prog = sheetProgress(detalle?.sheets);
        const nAbiertas = openTasks(detalle?.tasks).length;
        const nota = lastLogSummary(detalle?.lastLog);

        return (
            <React.Fragment key={job.id}>
                <tr onClick={() => handleJobSelect(job.id)}
                    className={`hover:bg-gray-50 cursor-pointer ${isFinished ? 'bg-gray-100 text-gray-400' : ''} ${expanded ? 'bg-blue-50' : ''}`}>
                    <td className="px-2 py-2 text-center">
                        <button
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(job.id); }}
                            title={expanded ? 'Ocultar detalle' : 'Ver planchas, tareas y bloqueo'}
                            className="text-gray-400 hover:text-gray-700 p-1"
                        >
                            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap font-mono text-sm text-center">{job.jobNumber || job.id}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-center">
                        {job.coverImageUrl
                            ? <img src={job.coverImageUrl} alt={job.jobName} onClick={(e) => { e.stopPropagation(); setLightbox({ url: job.coverImageUrl, title: job.jobName }); }} title="Clic para ampliar" className="w-12 h-12 object-cover rounded-md mx-auto cursor-zoom-in hover:ring-2 hover:ring-blue-500 transition" />
                            : <div className="w-12 h-12 rounded-md bg-gray-100 mx-auto flex items-center justify-center text-gray-300 text-xs">—</div>}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap font-medium text-sm text-center">{getClientName(job.clientId)}</td>
                    <td className="px-4 py-2 text-sm">
                        <div className="flex items-center justify-center gap-2">
                            <span>{job.jobName}</span>
                            {bloqueos.length > 0 && (
                                <span title={bloqueos.map(b => b.label).join(' · ')} className="flex-shrink-0 text-red-500"><AlertTriangle size={14} /></span>
                            )}
                        </div>
                        {/* Avance y nº de tareas INLINE: se ven sin expandir la fila. */}
                        {detalle && !detalle.loading && prog.sheetCount > 0 && (
                            <div className="mt-1.5 flex flex-col items-center">
                                <ProgressLine label="Imp." done={prog.printDone} total={prog.printTotal} barClass="bg-blue-500" />
                                <ProgressLine label="Corte" done={prog.cutDone} total={prog.cutTotal} barClass="bg-purple-500" />
                            </div>
                        )}
                        {detalle && !detalle.loading && nAbiertas > 0 && (
                            <div className="mt-1 flex justify-center">
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">
                                    <ListChecks size={11} /> {nAbiertas} tarea{nAbiertas === 1 ? '' : 's'}
                                </span>
                            </div>
                        )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-center">{job.createdAt?.toDate().toLocaleDateString()}</td>
                    <td className={`px-4 py-2 whitespace-nowrap text-sm text-center ${entregaClass}`}>
                        {job.deliveryDate || 'Sin fecha'}
                        {dias !== null && <span className="block text-[10px] font-normal">{dias < 0 ? `${Math.abs(dias)}d de atraso` : `en ${dias}d`}</span>}
                    </td>
                    {/* Notas: último evento del bus del job (jobs/{id}/ai_logs). */}
                    <td className="px-4 py-2 text-sm text-center max-w-[220px]">
                        {nota ? (
                            <span title={`${nota.date} · ${nota.full}`}>
                                <span className="font-mono text-[10px] text-gray-400 block">{nota.date}</span>
                                <span className="text-xs text-gray-600">{nota.text}</span>
                            </span>
                        ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-center">
                        <StatusMenu job={job} onStatusChange={handleStatusChange} />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center justify-center gap-4">
                            <button onClick={(e) => { e.stopPropagation(); handleEditJob(job); }} className="text-gray-500 hover:text-gray-800" title="Editar Trabajo"><Edit /></button>
                            <button onClick={(e) => { e.stopPropagation(); handleOpenJobChat(job); }} className="text-purple-600 hover:text-purple-900" title="Consultar con IA"><BrainCircuit /></button>
                            {user?.roles?.includes('Admin') && (
                                <button onClick={(e) => { e.stopPropagation(); handleDeleteJob(job.id); }} className="text-red-600 hover:text-red-900" title="Eliminar Trabajo (solo Admin)"><Trash2 /></button>
                            )}
                        </div>
                    </td>
                </tr>
                {expanded && <JobDetailRow job={job} data={enrich[job.id]} colSpan={COL_COUNT} blockOpts={blockOpts} />}
            </React.Fragment>
        );
    };

    const tableHead = (
        <thead className="bg-gray-50">
            <tr>
                <th className="w-10"></th>
                <SortableHeader field="jobNumber" label="ID" />
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <SortableHeader field="jobName" label="Trabajo" />
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Creación</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Entrega</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Notas</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
            </tr>
        </thead>
    );

    return (
        <div className="p-4 bg-gray-50 flex flex-col md:h-full md:overflow-hidden">
            <header className="flex justify-between items-center mb-4">
                <h1 className="text-3xl font-bold text-gray-800">Centro de Comando</h1>
                <button
                    onClick={() => setIsSystemChatOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 transition-colors"
                >
                    <BrainCircuit size={20}/> Asistente del Sistema
                </button>
            </header>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4">
                 <KPICard title="Trabajos Activos" value={dashboardKPIs.activeJobs} icon={<Activity size={32}/>} colorClass="bg-blue-500" />
                 <KPICard title="En Riesgo (Próx. 7 días)" value={dashboardKPIs.jobsAtRisk} icon={<Clock size={32}/>} colorClass="bg-yellow-500" />
                 <KPICard
                    title="Trabajos Bloqueados"
                    value={dashboardKPIs.blocked}
                    icon={<AlertTriangle size={32}/>}
                    colorClass="bg-red-500"
                    active={onlyBlocked}
                    onClick={() => setOnlyBlocked(v => !v)}
                 />
                 <KPICard title="Pendiente por Facturar" value={dashboardKPIs.pendingInvoice} icon={<FileCheck size={32}/>} colorClass="bg-purple-500" />
            </div>

            {/* Flujo de trabajo: trabajos por fase, clickeable para filtrar la tabla. */}
            <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Flujo de trabajo</h2>
                    <span className="text-xs text-gray-500">✅ {throughput30d} finalizados en 30 días</span>
                </div>
                <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3">
                    {WORKFLOW_PHASES.map(p => {
                        const active = statusFilter === p.value;
                        return (
                            <button
                                key={p.value}
                                onClick={() => setStatusFilter(active ? 'Todos' : p.value)}
                                title={active ? 'Quitar filtro' : `Filtrar por ${p.label}`}
                                className={`text-left p-3 rounded-lg border transition-all ${p.card} ${active ? 'ring-2 ring-offset-1 ring-gray-500' : 'hover:shadow-md'}`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className={`h-2.5 w-2.5 rounded-full ${p.dot}`}></span>
                                    <span className="text-xs font-semibold truncate">{p.label}</span>
                                </div>
                                <p className="text-2xl font-bold mt-1">{workflowByPhase[p.value] || 0}</p>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex items-center gap-4 mb-4">
                <input
                    type="text"
                    placeholder="Buscar por cliente, trabajo o ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-3 border rounded-lg shadow-sm"
                />
                <div className="relative">
                    <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="pl-10 pr-4 py-3 border rounded-lg shadow-sm appearance-none bg-white"
                    >
                        <option>Todos</option>
                        <option>En Progreso</option>
                        <option>Pendiente</option>
                        <option>Finalizado</option>
                        <option>Pendiente por Facturar</option>
                        <option>En Hold</option>
                        <option>URGENTE</option>
                    </select>
                </div>
                {/* Vista: agrupada por cliente (default) o la lista plana de siempre. */}
                <div className="inline-flex rounded-lg border shadow-sm overflow-hidden bg-white flex-shrink-0">
                    <button
                        onClick={() => setGroupBy('client')}
                        className={`flex items-center gap-2 px-4 py-3 text-sm border-r ${groupBy === 'client' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <Users size={16} /> Por cliente
                    </button>
                    <button
                        onClick={() => setGroupBy('flat')}
                        className={`flex items-center gap-2 px-4 py-3 text-sm ${groupBy === 'flat' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <List size={16} /> Lista plana
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto md:overflow-auto bg-white rounded-lg shadow md:flex-grow md:min-h-0">
                {isLoading ? (
                    <p className="text-center py-10 text-gray-500">Cargando trabajos...</p>
                ) : groupBy === 'flat' ? (
                    <table className="min-w-full divide-y divide-gray-200">
                        {tableHead}
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sortedAndFilteredJobs.length === 0 ? (
                                <tr><td colSpan={COL_COUNT} className="text-center py-10 text-gray-500">Ningún trabajo coincide con el filtro.</td></tr>
                            ) : sortedAndFilteredJobs.map(renderJobRow)}
                        </tbody>
                    </table>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        {tableHead}
                        {clientGroups.length === 0 ? (
                            <tbody><tr><td colSpan={COL_COUNT} className="text-center py-10 text-gray-500">Ningún trabajo coincide con el filtro.</td></tr></tbody>
                        ) : clientGroups.map(group => {
                            const collapsed = collapsedClients.has(group.clientId);
                            return (
                                <tbody key={group.clientId} className="divide-y divide-gray-200 border-b-2 border-gray-200">
                                    <tr onClick={() => toggleClient(group.clientId)} className="bg-gray-100 hover:bg-gray-200 cursor-pointer">
                                        <td colSpan={COL_COUNT} className="px-4 py-2.5">
                                            <div className="flex items-center gap-3">
                                                {collapsed ? <ChevronRight size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
                                                <span className="font-bold text-gray-800">{group.clientName}</span>
                                                <span className="text-xs text-gray-500">{group.jobs.length} trabajo{group.jobs.length === 1 ? '' : 's'}</span>
                                                <span className="flex-grow" />
                                                {group.noDate > 0 && (
                                                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                                                        {group.noDate} sin fecha
                                                    </span>
                                                )}
                                                {group.pendingFiles > 0 && (
                                                    <span
                                                        title="Planchas a las que les falta un archivo requerido y siguen en prepress"
                                                        className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                                                    >
                                                        {group.pendingFiles} plancha{group.pendingFiles === 1 ? '' : 's'} sin archivo
                                                    </span>
                                                )}
                                                {group.blockedCount > 0 && (
                                                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                                        {group.blockedCount} bloqueado{group.blockedCount === 1 ? '' : 's'}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {!collapsed && group.jobs.map(renderJobRow)}
                                </tbody>
                            );
                        })}
                    </table>
                )}
            </div>

            {/* Nota de los finalizados ocultos + toggle para traerlos. */}
            {!isLoading && (
                <div className="flex items-center gap-3 mt-3 text-xs text-gray-500 flex-shrink-0">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={includeFinished} onChange={(e) => setIncludeFinished(e.target.checked)} className="rounded" />
                        Incluir trabajos finalizados
                    </label>
                    {hiddenFinishedCount > 0 && (
                        <span>🗂 {hiddenFinishedCount} finalizado{hiddenFinishedCount === 1 ? '' : 's'} fuera de la vista.</span>
                    )}
                    {onlyBlocked && (
                        <button onClick={() => setOnlyBlocked(false)} className="text-red-600 font-semibold hover:underline">
                            Mostrando solo bloqueados — quitar filtro
                        </button>
                    )}
                </div>
            )}

            {/* Modal para un Trabajo Específico */}
            {isJobChatModalOpen && selectedJobForChat && (
                <AiChatModal
                    isOpen={isJobChatModalOpen}
                    onClose={() => setIsJobChatModalOpen(false)}
                    jobId={selectedJobForChat.id}
                    jobName={selectedJobForChat.jobName}
                    functions={functions}
                    user={user}
                    apiKey={apiKey}
                />
            )}

            {/* Asistente del Sistema: consulta trabajos, estados, tareas y problemas */}
            <SystemAiChatModal
                isOpen={isSystemChatOpen}
                onClose={() => setIsSystemChatOpen(false)}
                user={user}
            />

            {/* Lightbox: ver el preview del trabajo en grande */}
            {lightbox && (
                <div
                    className="fixed inset-0 z-[120] bg-black bg-opacity-80 flex items-center justify-center p-4"
                    onClick={() => setLightbox(null)}
                >
                    <button
                        onClick={() => setLightbox(null)}
                        className="absolute top-4 right-5 text-white text-4xl leading-none hover:text-gray-300"
                        title="Cerrar (Esc)"
                    >×</button>
                    <figure className="max-w-[90vw] max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                        <img src={lightbox.url} alt={lightbox.title || 'Preview'} className="max-w-[90vw] max-h-[82vh] object-contain rounded-lg shadow-2xl" />
                        {lightbox.title && <figcaption className="mt-3 text-white text-sm">{lightbox.title}</figcaption>}
                    </figure>
                </div>
            )}
        </div>
    );
};

export default DashboardView;
